import type { ReactNode } from "react";
import { weekdayLong, type BusinessLabels, type SessionNotBookedItem } from "@teamna/shared";
import { useSessionNotBooked } from "@teamna/web-client/hooks/use-coach-participants.ts";
import { Avatar, ErrorState, SkeletonList } from "../components/index.ts";

/**
 * One name the coach or the owner can pick for a session: a swimmer of the club or a name that
 * waits for a parent, with where they are (level, days, coaches). Someone else came uses it for the
 * Did not book list and for the search results; the session panel uses it for its Did not book tab.
 */
export function CandidateRow({ name, detail, action }: { name: string; detail: string; action?: ReactNode }) {
  return (
    <div className="flex items-center gap-3 rounded-card border border-line bg-paper px-3 py-2">
      <Avatar name={name} photoUrl={null} size={32} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[15px] font-semibold text-ink">{name}</span>
        <span className="block text-[12.5px] text-ink-2">{detail}</span>
      </span>
      {action}
    </div>
  );
}

/**
 * Did not book: the swimmers and waiting names of the session's level with no booking in it
 * (`GET .../sessions/:id/extras/not-booked`). Those who usually come on the session's day are first.
 * `action` puts a button on each row (Came today in Someone else came); the session panel has none.
 */
export function NotBookedList({
  businessId,
  sessionId,
  labels,
  openClassNext,
  action,
}: {
  businessId: string;
  sessionId: string;
  labels: Pick<BusinessLabels, "participantSingular" | "participantPlural">;
  /** What to do in an open class, where this place has it: the club search, or Add a participant. */
  openClassNext: string;
  action?: (item: SessionNotBookedItem) => ReactNode;
}) {
  const query = useSessionNotBooked(businessId, sessionId);
  if (query.isLoading && !query.data) return <SkeletonList rows={3} />;
  if (query.error && !query.data) return <ErrorState error={query.error} onRetry={() => void query.refetch()} />;
  const items = query.data?.items ?? [];
  const day = weekdayLong(query.data?.weekday ?? 0);
  if (query.data?.openToAll) {
    return <p className="rounded-card bg-sunken px-3 py-2.5 text-[13.5px] text-ink-2">This is an open class: any {labels.participantSingular} of the club can come, so there is no list. {openClassNext}</p>;
  }
  if (items.length === 0) {
    return <p className="rounded-card bg-sunken px-3 py-2.5 text-[13.5px] text-ink-2">All the {labels.participantPlural} of this level booked this session.</p>;
  }
  const usual = items.filter((item) => item.usualDay).length;
  return (
    <div className="flex flex-col gap-2">
      <p className="text-[13px] text-ink-2">
        {items.length} {items.length === 1 ? labels.participantSingular : labels.participantPlural} of this level did not book.
        {usual === items.length
          ? ` ${items.length === 1 ? "They usually come" : "They all usually come"} on ${day}.`
          : usual
            ? ` ${usual} usually ${usual === 1 ? "comes" : "come"} on ${day}: they are first.`
            : ""}
      </p>
      {items.map((item) => (
        <CandidateRow key={`${item.kind}-${item.id}`} name={item.name} detail={item.usualDay ? `Usually comes on ${day}` : item.where} action={action?.(item)} />
      ))}
    </div>
  );
}
